/**
 * @quotech/ui — StatusBadge
 *
 * Variant-mapped status badge for the common job / quote / invoice
 * lifecycle states (active, draft, won, lost, paid, overdue, …).
 *
 * Lifted from apps/sam + apps/steel on 2026-05-04. The two copies
 * were byte-for-byte identical (51 lines × 2). Both apps now
 * import from `@quotech/ui`.
 *
 * NOTE: workforce / admin / cfo-intercompany / crm-quote-drawer
 * each carry their own DOMAIN-SPECIFIC StatusBadge components
 * (employee statuses / system health / intercompany states /
 * quote-drawer states). Those are intentionally separate — they
 * would benefit from being renamed (`EmployeeStatusBadge`,
 * `IntercompanyStatusBadge`, …) but consolidating them into one
 * universal would be wrong (their status vocabularies don't
 * overlap).
 *
 * USAGE:
 *   import { StatusBadge } from '@quotech/ui'
 *   <StatusBadge status="won" />
 *
 * Custom statuses fall through to the `default` variant.
 */
import { cn } from '../lib/utils'

export type StatusBadgeVariant =
  | 'success' | 'warning' | 'error' | 'info'
  | 'default' | 'active' | 'completed' | 'prospect'

const statusMap: Record<string, StatusBadgeVariant> = {
  active: 'active',
  in_progress: 'active',
  deposit: 'warning',
  paid: 'success',
  completed: 'completed',
  sent: 'info',
  draft: 'default',
  pricing: 'active',
  review: 'prospect',
  chasing: 'warning',
  on_hold: 'warning',
  won: 'success',
  overdue: 'error',
  prospect: 'prospect',
  accepted: 'success',
  rejected: 'error',
  lost: 'error',
  dropped: 'warning',
  never_done: 'default',
  cancelled: 'default',
  ordered: 'info',
  in_transit: 'warning',
  partial: 'warning',
  delivered: 'success',
  pending: 'default',
}

const styles: Record<StatusBadgeVariant, string> = {
  success: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
  warning: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30',
  error: 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30',
  info: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30',
  default: 'bg-secondary text-muted-foreground border-border',
  active: 'bg-primary/15 text-primary border-primary/30',
  completed: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
  prospect: 'bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/30',
}

export interface StatusBadgeProps {
  status: string
  className?: string
}

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const variant = statusMap[status.toLowerCase().trim()] ?? 'default'
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md border', styles[variant], className)}>
      {status}
    </span>
  )
}
